/**
 * Simulation & Computation Action Definitions
 *
 * Actions for energy calculations, docking, forcefield parameterization,
 * molecular properties, and simulation case management.
 *
 * @module gibbs/registry/actions/simulation
 */

import { WorkflowTypes as T } from '../types';
import { param, pre, post } from '../schema';

// ─── Energy Minimization ────────────────────────────────────────────────────

export const energyMinimize = {
    id: 'energy-minimize',
    name: 'Energy Minimization',
    category: 'Simulation & Computation',
    description: 'Minimize the potential energy of one or more compounds in the context of the protein binding site. Supports rigid (ligand only) and flexible (ligand + nearby protein residues) minimization with different engines.',
    inputs: [
        param('compounds', T.CompoundList, 'Compounds to minimize'),
        param('caseData', T.CaseData, 'Protein context'),
    ],
    outputs: [
        param('minimizedCompounds', T.CompoundList, 'Compounds with optimized coordinates'),
        param('energies', T.EnergyBreakdown, 'Updated energy values'),
        param('solvation', T.PropertySet, 'Solvation energy terms'),
    ],
    // `EnergyMinimize` is shared with the rest of the app and returns `compounds`.
    // Without this, `step:N.minimizedCompounds` — the edge promptTemplates teaches
    // the planner to write — never resolved, and the downstream step silently fell
    // back to the single active compound.
    outputAliases: { minimizedCompounds: 'compounds' },
    parameters: [
        param('minimizationType', 'string', 'Minimization type (only Rigid is currently supported)', false, { defaultValue: 'Rigid', options: ['Rigid'] }),
        param('minimizationEngine', 'string', 'BFD or MM', false, { options: ['BFD', 'MM'] }),
        param('energyEngine', 'string', 'Energy calculation engine', false, { options: ['BFD', 'MM'] }),
        param('forcefield', 'string', 'Force field (e.g., Amber)', false),
        param('minimizationSteps', 'number', 'Max optimization steps', false),
        param('residueCutoff', 'number', 'Radius for protein residues', false),
        param('useUnboundConf', 'boolean', 'Use unbound conformation', false, { defaultValue: false }),
        param('skipSolvation', 'boolean', 'Skip solvation calculation', false, { defaultValue: false }),
        param('batchSize', 'number', 'Compounds per batch', false, { defaultValue: 1 }),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('compound_loaded', 'Compounds must be loaded'),
    ],
    postconditions: [
        post('compound_has_energies', 'Compound energies are updated'),
    ],
    runtime: 'long',
    status: 'implemented',
    userAction: 'EnergyMinimize',
    serverCommand: 'energy-minimize',
    jobType: null,
};

// ─── Calculate Binding Energies ─────────────────────────────────────────────

export const getEnergies = {
    id: 'get-energies',
    name: 'Calculate Binding Energies',
    category: 'Simulation & Computation',
    description: 'Calculate binding energy components (van der Waals, electrostatic, solvation) for compounds without changing their coordinates. Provides energy breakdown for scoring and ranking.',
    inputs: [
        param('compounds', T.CompoundList, 'Compounds to evaluate'),
    ],
    outputs: [
        param('energies', T.EnergyBreakdown, 'Per-compound energy breakdown: {vdw, electrostatic, solvation (ddGs), stress, interaction_total, amber_total, energy_sum} in kcal/mol'),
    ],
    parameters: [
        param('energyEngine', 'string', 'Energy calculation engine', false, { options: ['BFD', 'MM'] }),
        param('forcefield', 'string', 'Force field', false),
        param('residueCutoff', 'number', 'Protein residue inclusion radius', false),
        param('useUnboundConf', 'boolean', 'Use unbound conformation', false, { defaultValue: false }),
        param('skipSolvation', 'boolean', 'Skip solvation energy', false, { defaultValue: false }),
        param('batchSize', 'number', 'Batch size', false, { defaultValue: 1 }),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('compound_loaded', 'Compounds must be loaded'),
    ],
    postconditions: [
        post('compound_has_energies', 'Compound energies are updated'),
    ],
    runtime: 'short',
    status: 'implemented',
    userAction: 'GetEnergies',
    serverCommand: 'get-energies-for-ligand',
    jobType: null,
};

// ─── Calculate Solvation Energy ─────────────────────────────────────────────

export const getSolvation = {
    id: 'get-solvation',
    name: 'Calculate Solvation Energy',
    category: 'Simulation & Computation',
    description: 'Calculate solvation energy terms (desolvation penalty) for compounds. Usually called as part of energy calculations.',
    inputs: [
        param('compounds', T.CompoundList, 'Compounds with existing vdW/electrostatic energies'),
    ],
    outputs: [
        param('solvation', T.PropertySet, 'Solvation energy values (ddGs) in kcal/mol'),
    ],
    parameters: [],
    dependsOn: ['get-energies'],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('compound_loaded', 'Compounds must be loaded'),
        pre('compound_has_energies', 'Compounds must have vdW/electrostatic energies calculated'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'GetSolvation',
    serverCommand: 'get-solvation-for-ligand',
    jobType: null,
};

// ─── Calculate Forcefield Parameters ────────────────────────────────────────

export const getForcefieldParameters = {
    id: 'get-forcefield-parameters',
    name: 'Calculate Forcefield Parameters',
    category: 'Simulation & Computation',
    description: 'Compute partial charges and AMBER atom types for compounds. Required before energy calculations. Automatically applied during minimization.',
    inputs: [
        param('compounds', T.CompoundList, 'Ligand compounds (not proteins)'),
    ],
    outputs: [
        param('parameters', T.PropertySet, 'Per-atom charges and AMBER types'),
    ],
    parameters: [
        param('forcefield', 'string', 'Force field from preferences', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Compounds must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'GetForcefieldParameters',
    serverCommand: 'get-forcefield-params',
    jobType: null,
};

// ─── Dock Compound (Local) ──────────────────────────────────────────────────

export const dockCompound = {
    id: 'dock-compound',
    name: 'Dock Compound (Local)',
    category: 'Simulation & Computation',
    description: 'Dock compounds using AutoDock or DiffDock via the LOCAL BFD server. This is the standard docking action for most workflows. For GPU-accelerated GNINA docking (distributed job), use job-gnina-docking or job-gnina-flexible-docking instead.',
    inputs: [
        param('compounds', T.CompoundList, 'Compounds to dock'),
        // dockingParams is optional as an input because the individual
        // fields (dockingProgram, speedArg, poseCount, boxParams) are
        // already declared as parameters below. CmdInterpreter assembles
        // them into a single options object at dispatch time, which
        // submitDocking treats as dockingParams.
        param('dockingParams', 'object', 'Docking configuration', false),
    ],
    outputs: [
        param('dockedPoses', T.CompoundList, 'Docked compounds with binding poses'),
        param('scores', T.PropertySet, 'Per-pose affinity scores'),
    ],
    // Same defect as energy-minimize: `submitDocking` returns `compounds`, and
    // promptTemplates advertises `step:1.dockedPoses` in its worked examples.
    outputAliases: { dockedPoses: 'compounds' },
    parameters: [
        param('dockingProgram', 'string', 'Docking program', false, { options: ['autodock', 'diffdock'] }),
        param('speedArg', 'string', 'Speed preset', false, { options: ['accurate', 'fast', 'faster'] }),
        // Same range the Docking pane offers. Declared here so every surface
        // rendering this parameter constrains it identically instead of relying
        // on one form's own validation.
        param('poseCount', 'number', 'Number of poses to generate', false, {
            constraints: { min: 1, max: 10 },
        }),
        param('boxParams', T.DockingBox, 'Docking search box: {center_x, center_y, center_z, size_x, size_y, size_z}', false),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('compound_loaded', 'Compounds must be loaded'),
    ],
    postconditions: [],
    runtime: 'long',
    status: 'implemented',
    userAction: 'SubmitDocking',
    serverCommand: 'dock-compound',
    jobType: null,
};

// ─── GIFE Energy Calculation ────────────────────────────────────────────────

export const getGifeEnergies = {
    id: 'get-gife-energies',
    name: 'GIFE Energy Calculation',
    category: 'Simulation & Computation',
    description: 'Fetch Graph Isomorphism-based Force Evaluation (GIFE) energies for atoms or atom groups via the GIFE service.',
    inputs: [
        param('atoms', 'object', 'Specific atoms to evaluate', false),
        param('atomGroups', 'object', 'Atom groups to evaluate', false),
    ],
    outputs: [
        param('energies', 'object', 'Energy breakdown per atom/group (GiFEResultUnit[])'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'Compounds must be loaded'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'GetGifeEnergies',
    serverCommand: null,
    jobType: null,
};

// ─── Get Molecule Properties ────────────────────────────────────────────────

export const getMoleculeProperties = {
    id: 'get-molecule-properties',
    name: 'Get Molecule Properties',
    category: 'Simulation & Computation',
    description: 'Retrieve physicochemical properties for a molecule specification.',
    inputs: [
        param('spec', 'string', 'Molecule specification'),
    ],
    outputs: [
        param('properties', T.PropertySet, 'Computed properties'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [],
    postconditions: [],
    runtime: 'interactive',
    status: 'implemented',
    userAction: null,
    serverCommand: 'get-molecule-properties',
    jobType: null,
};

// ─── Generate Unbound Conformations ─────────────────────────────────────────

export const getUnboundConformations = {
    id: 'get-unbound-conformations',
    name: 'Generate Unbound Conformations',
    category: 'Simulation & Computation',
    description: 'Generate unbound (free) conformations of a ligand, separate from protein context. Used for strain energy calculations and binding analysis.',
    inputs: [
        param('values', 'object', 'Configuration for conformation generation'),
    ],
    outputs: [
        param('conformations', T.CompoundList, 'Unbound ligand conformations'),
    ],
    parameters: [],
    dependsOn: [],
    preconditions: [
        pre('compound_loaded', 'A compound must be loaded'),
    ],
    postconditions: [],
    runtime: 'long',
    status: 'implemented',
    userAction: null,
    serverCommand: 'get-unbound-conformations',
    jobType: null,
};

// ─── Validate Simulation Setup ──────────────────────────────────────────────

export const prepareProjectCase = {
    id: 'prepare-project-case',
    name: 'Validate Simulation Setup',
    category: 'Simulation & Computation',
    description: 'Validate that a simulation case is properly configured before running. Checks residue count, hydrogens, binding site specification, partial charges, and storage. Returns detailed status with any errors.',
    inputs: [
        param('simSpec', T.SimSpec, 'Simulation specification with protein chains, residues, binding sites'),
    ],
    outputs: [
        param('status', 'string', 'Validation result: Ready | Conflict | TooManyChainsError | NoProteinChainsError | NoHydrogensError | StorageUpdatedError | PersonalStorageError | BindingSiteSpecError | FdbFileError | PartialChargesError | UnknownError'),
        param('errors', 'object', 'Detailed error messages'),
        param('case_suggestion', 'string', 'Alternative case name if conflict', false),
    ],
    parameters: [
        param('useSelection', 'boolean', 'Only simulate selected atoms', false, { defaultValue: false }),
        param('dontDeselect', 'boolean', 'Keep selection after validation', false, { defaultValue: false }),
    ],
    dependsOn: [],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded with full case data'),
        pre('simulation_ready', 'Simulation environment must be configured'),
    ],
    postconditions: [],
    runtime: 'short',
    status: 'implemented',
    userAction: 'PrepareProjectCase',
    serverCommand: 'validate-project-case',
    jobType: null,
};

// ─── Run Simulation ─────────────────────────────────────────────────────────

export const runProjectCase = {
    id: 'run-project-case',
    name: 'Run Simulation',
    category: 'Simulation & Computation',
    description: 'Execute a prepared simulation case (GCMC-SACP fragment simulation). This generates the fragment binding data (Boltzmann Maps) for a protein.',
    inputs: [
        param('simSpec', T.SimSpec, 'Validated simulation specification'),
        param('notifyUrl', 'string', 'Webhook URL for completion notification'),
    ],
    outputs: [
        param('jobStatus', 'string', 'Simulation submitted to server'),
    ],
    parameters: [],
    dependsOn: ['prepare-project-case'],
    preconditions: [
        pre('protein_loaded', 'A protein must be loaded'),
        pre('simulation_ready', 'prepare-project-case must return Ready'),
    ],
    postconditions: [],
    runtime: 'background',
    status: 'implemented',
    userAction: 'RunProjectCase',
    serverCommand: 'run-project-case',
    jobType: null,
};

// ─── Default Export ─────────────────────────────────────────────────────────

export default [
    energyMinimize,
    getEnergies,
    getSolvation,
    getForcefieldParameters,
    dockCompound,
    getGifeEnergies,
    getMoleculeProperties,
    getUnboundConformations,
    prepareProjectCase,
    runProjectCase,
];
